import {ChartTypeEnum} from  "@/components/explorer/visualizations/ChartTypeEnum.js"
import * as Accessibility from "@/components/accessibility/accessibility.js";
import _Highcharts from "highcharts/modules/treemap";
import markdownit from 'markdown-it'
import { event } from 'vue-gtag'
import  * as AppliedValues from "@/components/explorer/visualizations/AppliedValues.js";

const version = __APP_VERSION__

const md = markdownit()

class ChartSettings {
    constructor(xAxisDim, seriesDim, type) {
      	this.xAxisDim = xAxisDim;
      	this.seriesDim = seriesDim;
		this.type = type;
    }
}

async function defineChartProps(type, indicatorObj, chartSettings, { onMoreClick }) {

	let yValue = indicatorObj.valuesLabel;
	let allFilters = getCategoryFilters(indicatorObj)

	chartSettings.request =  AppliedValues.buildRequest(indicatorObj, chartSettings, yValue, allFilters);
	if(import.meta.env.VITE_APP_DEBUG)
		console.log('chartSettings', chartSettings)

	AppliedValues.applyRequestFilters(indicatorObj, type, chartSettings.request);

	chartSettings.request.categories.forEach(cat => {
		if(cat.filters.length == 0) {
			throw new Error("No records found");
		}
	})

	let results = await fetchData(chartSettings.request)
	//console.log('response Treemap chart',results)
	if(Object.keys(results).length == 0 || Object.keys(Object.values(results)[0]).length == 0) {
	 	throw new Error("No records found");
	} else {
		let noData = true;
		const isEmpty = (obj) => Object.keys(obj).length === 0;

		Object.entries(results).forEach(([key, item]) => {
			const truthyOnly = Object.fromEntries(
				Object.entries(item).filter(([_, value]) => !!value)
			);
			if(!isEmpty(truthyOnly)) {
				noData = false;
			}
		});
		if(noData) {
			throw new Error("No records found");
		}
	}

	let props = buildChartJson(type, indicatorObj, yValue, chartSettings, results, { onMoreClick })
	if(import.meta.env.VITE_APP_DEBUG)
		console.log('chartProps',props)
	return props;
}

async function fetchData(request) {
	const apiUrl = import.meta.env.VITE_API_URL;
	let reqDims = request.xAxis + ',';
	let reqFilters = '';
	request.categories.forEach((cat) => {
		if(cat.selected) {
			reqDims += cat.categoryName;
		}
		reqFilters += cat.categoryName + ': ' + cat.filters.join() + '|';
	})
	event('filter_dataset', { 'indicator' : request.datasetName, 'dimensions': reqDims, 'filter' : reqFilters.slice(0,100).slice(0,-1) })

	let url = apiUrl + '/indicators/api/chartdata'
	let response = await fetch(url, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json; charset=UTF-8',
			'Accept': 'application/json; charset=UTF-8',
			'x-api-version': version
		},
			body: JSON.stringify(request)
		}
	)
	if (!response.ok) {
		throw new Error(`HTTP status: ${response.status}`);
	}
	return await response.json()
}

const buildChartJson = (type, indicatorObj, yValue, chartSettings, result, { onMoreClick }) => {

	const valuesUnit = indicatorObj.valuesUnit	
	const usdFormatter = new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD', minimumFractionDigits: indicatorObj.decimalPrecision, maximumFractionDigits: 2});
	const pctFormatter = new Intl.NumberFormat('en-US', {minimumFractionDigits: indicatorObj.decimalPrecision, maximumFractionDigits: 2});
	const floatFormatter = new Intl.NumberFormat('en-US', {minimumFractionDigits: indicatorObj.decimalPrecision, maximumFractionDigits: 100});
	const numFormatter = new Intl.NumberFormat('en-US', {minimumFractionDigits: 0, maximumFractionDigits: 100});
	//TODO: Enable new formatter logic if Float valuesUnit implemented
	let formatter = valuesUnit == "Currency"?numFormatter:valuesUnit == "Percent"?pctFormatter:valuesUnit=="Float"?floatFormatter:numFormatter
	//let formatter = valuesUnit == "Currency"?numFormatter:valuesUnit == "Percent"?pctFormatter:numFormatter
	const valuesMultiplier = (indicatorObj.chartMultiplier ? parseInt(indicatorObj.chartMultiplier) : 1);
	//const valuesRoundingUnit = (indicatorObj.roundingUnit && indicatorObj.roundingUnit.length > 0 ? '(' + indicatorObj.roundingUnit.replace(/[$%]/g, "") + ')' : null);
	const valuesRoundingUnit = (indicatorObj.roundingUnit && indicatorObj.roundingUnit.length > 0 ? '(' + indicatorObj.roundingUnit + ')' : null);

	const classNameBase = Accessibility.readUserPreference('patternfill') === 'true' ? 'indicators-treemap-series-pattern-' : 'indicators-treemap-series-';
	const classNameIconSuffix = Accessibility.readUserPreference('patternfill') === 'true' ? '-icon' : '';

	let ctype = type != null?type:'treemap'
   	let chartprops = {
		lang: {
			locale: 'en-US'
		},
		chart: {
			type: ctype,
			styledMode: true,
			// Explicitly tell the width and height of a chart
			//height: 560,
			height: 0,
			width: null,
			events: {
				render() {
					//add Full Source button link to footer to support mobile format
					const links = this.container.querySelectorAll('.footnote-more');
					if(links != null) {
						links.forEach(link => {
							if (link && !link.dataset.bound) {
								link.dataset.bound = 'true';

								link.addEventListener('click', (e) => {
									e.preventDefault();
									console.log('clicked', e)
									onMoreClick(e)
								});
							}
						})
					}

					//reformat title/subtitle to support wrapped labels in mobile layout
					const chart = this;
					if (!chart.title || !chart.subtitle) {
						return;
					}
					const titleBBox = chart.title.getBBox();
					if(titleBBox.height < 30) {
						chart.subtitle.attr({
							y: chart.title.alignAttr.y + titleBBox.height 
						});
					} else {
						chart.title.attr({
							y: titleBBox.height - 50
						});						
						chart.subtitle.attr({
							y: titleBBox.height
						});
					}
					const caption = this.caption;
					if (caption && caption.css) {
						caption.css({
							width: (this.chartWidth - 40) + 'px'
						});
					}								
				}
			}
		},
		credits: {
			enabled: false
		},
		title: {
			text: '',
		},
		subtitle: {
			text: ''
		},
		legend: {
			maxHeight: 150,
			title: {
				text: ''
			}
		},
		plotOptions: {
			treemap: {
				states: {
					hover: {
						lineWidthPlus: 5,
						opacity: 1
					}
				}
      		}
    	},
		series: [],
		responsive: {
		rules: [{
			condition: {
			maxWidth: 600
			},
			chartOptions: {
			legend: {
				enabled: false
			}
			}
		}]
    },
    exporting: {
		chartOptions: {
			chart: {
				type: ctype,
				style: {
					//prevents warning on fetching font files that are not needed
					fontFamily: 'Roboto,sans-serif'
				},
				spacingBottom: 10
			}
		},
		plotOptions: {
			treemap: {
				levelIsConstant: false
			}
		},
		buttons: {
			contextButton: {
				enabled: false // hides the built-in button
			}
		},
		local: true,
		libURL: './highcharts-libs/',
		fallbackToExportServer: false, // use offline export only
		applyStyleSheets: true,
		useHTML: false
    },
    tooltip: {
      formatter: function() {
		let title = yValue;
		if(valuesRoundingUnit) {
			title += ' ' + valuesRoundingUnit;
		}
		let header =`<div class="header"><span class="title">${title}</span>`;
        if(this.drillId == false) {
          if(this.series.rootNode.length > 0) {
			//TODO: Add '$' prefix
            //const value = valuesUnit == "Currency" ? usdFormatter.format(this.value) : numFormatter.format(this.value)
            const value = valuesUnit == "Currency" ? floatFormatter.format(this.value) : valuesUnit=="Float"?floatFormatter.format(this.value):numFormatter.format(this.value)				
			//const value = numFormatter.format(this.value);
			const icon = `<span class="tt-circle ${this.className}${classNameIconSuffix}"><svg width="10px" height="10px"><circle r="5px" cx="5px" cy="5px"/></svg></span>`;
            const body = `${icon}<span class="explorer-tool-tip">${this.name}: <b>${value}</b></span>`
			let tip = 
				`<div class="explorer-tool-tip">
					${header}
						<div class="label">${chartSettings.seriesDim.columnName}: ${this.parent.replaceAll('_',' ')}</div>
					</div>
					${body}
				</div>`;
			return tip; 
          } else {
            return false;
          }
        } else {
          	let body = '';
			data.forEach(i => {
				if(i.parent == this.name.replaceAll(' ', '_') && !Number.isNaN(i.value)) {
					const thisParticularClassName = i.className.split(' ')[1] + classNameIconSuffix
					//TODO: Add '$' prefix
					//const value = valuesUnit == "Currency" ? usdFormatter.format(i.value) : numFormatter.format(i.value);
					const value = valuesUnit == "Currency" ? floatFormatter.format(i.value) : valuesUnit=="Float"?floatFormatter.format(i.value):numFormatter.format(i.value)				
					//const value = numFormatter.format(i.value) 
					const icon = `<span class="tt-circle ${thisParticularClassName}"><svg width="10px" height="10px"><circle r="5px" cx="5px" cy="5px"/></svg></span>`;
					body += `${icon}${i.name}: <b>${value}</b><br />`;
				}
			})
          //return `${header}<br/>${chartSettings.seriesDim.columnName}: ${this.name}<br/>${chartSettings.xAxisDim.columnName}:<br />${body}`;
		   
			let tip = 
				`<div class="explorer-tool-tip">
					${header}
						<div class="label">${chartSettings.seriesDim.columnName}:  ${this.name}</div>
						<div class="label">${chartSettings.xAxisDim.columnName}:</div>
					</div>
					${body}
				</div>`;
			return tip		 
		  //return `<div class="explorer-tool-tip">${header}<br/>${chartSettings.seriesDim.columnName}: ${this.name}<br/>${chartSettings.xAxisDim.columnName}:</div>${body}</div>`; 
		}		  

      },
      positioner: function (labelWidth, labelHeight, point) {
        if(this.chart.hoverPoint.options.parent) {
          return { x: point.plotX - (labelWidth/2), y: this.chart.plotTop + point.plotY - (labelHeight + 18) };//this.chart.hoverPoint.shapeArgs.height}; //- labelHeight
        } else {
          if((this.chart.hoverPoint.shapeArgs.y + labelHeight - this.chart.plotBox.height) < 0) {
            return { x: point.plotX - (labelWidth/2), y: this.chart.plotTop + 18};
          } else {
            return { x: point.plotX - (labelWidth/2), y: this.chart.plotTop + this.chart.hoverPoint.shapeArgs.y  - (labelHeight)};
          }
        }
      },
      shape: 'rect',
      fixed: false,
      useHTML: true,
      outside: true,
      shared: false, // or true depending on default or user config
    }
  }

	//chartprops.title.text = indicatorObj.valuesLabel;
	//chartprops.title.text += '<br /><span class="highcharts-subtitle">Click points to drill down</span>';
	/*if(valuesRoundingUnit) {
		chartprops.title.text += ' ' + valuesRoundingUnit;
	}*/
	let title = `${indicatorObj.valuesLabel} by ${chartSettings.xAxisDim.columnName} and ${chartSettings.seriesDim.columnName}`
	let year = indicatorObj.columns.items.find(x => x.columnName == 'Year')
	if(year) {
		const minYear = Math.min(...year.appliedValues.filter(x => x.selected).map(item => Number(item.name)));
		const maxYear = Math.max(...year.appliedValues.filter(x => x.selected).map(item => Number(item.name)));
		if(minYear == maxYear)
			title += ": " + minYear
		else
			title += ": " + minYear + "\u2013" + maxYear
	}
	chartprops.title.text = title

	let filters = ''
	let cols = 	indicatorObj.columns.items.filter(c => {
		if(c.valueFlag == 0 && c.categoryLabelFlag == 1 && c.columnName != 'Year'
			&& c.columnName != chartSettings.xAxisDim.columnName
			&& c.columnName != chartSettings.seriesDim.columnName
			&& c.columnName != chartSettings.seriesDim.parentColumnName
			&& c.columnName != chartSettings.seriesDim.childColumnName
			&& c.childColumnName == null) {
				return c
			}
		})
	cols.forEach((c, n) => {
		let selected = "<span class='section-break'>" + c.columnName + ": "
		if(c.appliedValues && c.appliedValues.length > 0) {
			let all = c.totalLabel 
			let values = c.appliedValues.filter(x => x.selected && x.name != all)
			if(values.length > 0) {
				values.forEach((v, i) => selected += v.name + (i < values.length-1?', ': n < cols.length -1?'; ':''))
				filters += selected + "</span>"
			}
		}
	})
	chartprops.subtitle.text = filters

	let availableData = Object.entries(result);

	//Build series array which contains all data point for a bar
	let catName = chartSettings.seriesDim.columnName
	//chartprops.title.text = chartSettings.xAxisDim.columnName;
	//EXPERIMENT:
	let data =[];
	let seriesObj = {};
	let seriesExpObj = {};
	seriesObj.type = "treemap";
	seriesObj.name = catName;
	//seriesObj.levelIsConstant = false;
	seriesObj.allowTraversingTree = true;
	//seriesObj.alternateStartingDirection = false;
	seriesObj.groupPadding = 8;
    //seriesObj.borderColor = '#000000';
    //seriesObj.borderRadius = 3;
	//seriesObj.borderWidth = 16,
	//seriesObj.nodeSizeBy = 'leaf';
	//seriesObj.layoutAlgorithm = 'strip';
	seriesObj.layoutAlgorithm = 'squarified';
	seriesObj.levels = [{
    level: 1,
    borderWidth: 3,
    borderColor: '#0f0101ff',
    dataLabels: [{
      //headers: true,
      enabled: true,
      useHTML: true,
      className: 'indicators-treemap-labels indicators-treemap-labels-level-1',
      formatter: function () {
        //let val = numFormatter.format(this.value);
		let val = formatValue(this.value, valuesMultiplier, indicatorObj.decimalPrecision)
		//TODO: Add '$' prefix
        return this.name + (valuesUnit == "Currency"?' $':' ') + val
		//return this.name + ' ' + val
      },
      align: 'left',
      verticalAlign: 'top'
    }]
  }, {
    level: 2,
    //borderWidth: 3,
    //borderColor: 'red',
    dataLabels: [{
      //headers: false,
      enabled: true,
      useHTML: true,
      className: 'indicators-treemap-labels indicators-treemap-labels-level-2',
      formatter: function () {
        //let val = numFormatter.format(this.value);
		let val = formatValue(this.value, valuesMultiplier, indicatorObj.decimalPrecision)
        
		//TODO: Add '$' prefix
		return '<span class="' + this.className + '">' + this.name + (valuesUnit == "Currency"?' $':' ') + val + '</span>'
		//return '<span class="' + this.className + '">' + this.name + ' '+ val + '</span>'
      },
      align: 'center',
      verticalAlign: 'middle'
    }]
  }];

  seriesExpObj.levels = []
  seriesObj.levels.forEach(lvl => {
    const expLvl = {}
    expLvl.level = lvl.level
    expLvl.dataLabels = []
    const dtLbl = {}
    dtLbl.enabled = true
    dtLbl.headers = true
    dtLbl.useHTML = false
    dtLbl.className = lvl.dataLabels[0].className
    dtLbl.formatter = function () {
      const width = this.point.shapeArgs?.width || 50;

      // Hide labels for very small boxes
      /*if (width < 30) {
        return '...';
      } */
      //let val = numFormatter.format(this.value); 
	  let val = formatValue(this.value, valuesMultiplier, indicatorObj.decimalPrecision)
	  //TODO: Add '$' prefix
      let lblText = this.name + (valuesUnit == "Currency"?' $':' ') + val ;
	  //let lblText = this.name + ' ' + val ;
      let lblFormatted = truncateLabel(lblText, width - 8); // padding
      let lbl = '<span class="' + this.className + '">' + lblFormatted + '</span>'
      //console.log('LBL', lbl)
      return lbl
    },
    dtLbl.align = lvl.dataLabels[0].align
    dtLbl.verticalAlign = lvl.dataLabels[0].verticalAlign

    expLvl.dataLabels.push(dtLbl)
    seriesExpObj.levels.push(expLvl)
  })
  chartprops.exporting.chartOptions.series = []
  chartprops.exporting.chartOptions.series.push(seriesExpObj)

	let b = 0;
	availableData.forEach(([cat, obj]) => {
		//parents:
		let indicatorsClassName = 'indicators-treemap ' + classNameBase + b % 6;
		//indicatorsClassName += ' indicators-chart-series-pattern-0'; // + this.series.colorIndex % 10;
		/*if(Accessibility.readUserPreference('colorblind') === 'achromatopsia') {
			indicatorsClassName = 'indicators-treemap indicators-chart-series-' + b % 10;
		}*/
		data.push({id: cat.replaceAll(' ','_'), name: cat, className: indicatorsClassName, colorIndex: b});
		let c = 0;
		Object.entries(obj).forEach(([key, val]) => {
			//children:
			let indicatorsChildClassName = 'indicators-treemap ' + classNameBase + ((b + c) % 6);
			/*if(Accessibility.readUserPreference('colorblind') === 'achromatopsia') {
				indicatorsChildClassName = 'indicators-treemap indicators-chart-series-' + (b + c) % 10;
			}*/
			//if(Accessibility.readUserPreference('colorblind') === 'achromatopsia') {
				//indicatorsChildClassName = ' indicators-chart-series-pattern-0'; // + this.series.colorIndex % 10;
			//}
			const theValue = !isNaN(val) ? parseFloat(val) : Number(val.replaceAll(",", ""));
			data.push({
				name: key,
				parent: cat.replaceAll(' ','_'),
				className: indicatorsChildClassName,
				value: theValue //!isNaN(theValue) ? theValue : 0
			});
			c++;
		});
		b++;
	});

	// indicators-treemap-label-achromatopsia
	/*if(Accessibility.readUserPreference('colorblind') === 'achromatopsia') {
		seriesObj.levels[1].dataLabels[0].className = 'indicators-treemap-labels indicators-treemap-labels-level-2 indicators-treemap-label-achromatopsia';
	}*/
	seriesObj.data = data;
	//console.log('-->treemap seriesObj: ', seriesObj)
	//console.log('-->treemap data: ', seriesObj.data)
	chartprops.series.push(seriesObj);

	const abbr = indicatorObj.abbreviations ? indicatorObj.abbreviations.join('; ') + '.' : '';
	const src = md.renderInline(indicatorObj.source);
	//srcHtml is used in the footer mobile modal
	chartSettings.srcHtml = src
	chartSettings.keyHtml = indicatorObj.abbreviations ? indicatorObj.abbreviations.join('.<br />') + '.' : '';

	let footerNotes = `<div class="explorer-footnote-wrapper">`
	if(indicatorObj.abbreviations && indicatorObj.abbreviations.length > 0) {
		footerNotes += `	<span class="explorer-footnote"><span class="title">Key:</span> ${abbr} </span>`
		footerNotes += `	<button class="btn-tertiary footnote-more" data-value='abbreviations'>Full Key</button><br />`
	}
	footerNotes += `	<button class="btn-tertiary explorer-footnote-link" data-value='source'>See full data notes</button><br/>`
	footerNotes += `	<span class="explorer-footnote"><span class="title">Source(s):</span> ${src} </span>`
	footerNotes += `	<button class="btn-tertiary footnote-more" data-value='source'>Full Source</button>`
	footerNotes += `</div>`

  	let footerNotesExp = `<div class="explorer-footnote-wrapper">`
	if(indicatorObj.abbreviations && indicatorObj.abbreviations.length > 0) {
		footerNotesExp += `<span class="explorer-footnote"><span class="title">Key:</span> ${abbr} </span><br/>`
	}
	footerNotesExp += `<span class="explorer-footnote"><span class="title">Source(s):</span> ${src} </span></div>`

	let footer = {}
	footer.align = "left"
	footer.verticalAlign = "bottom"
	footer.useHTML = true
	footer.text = footerNotes
	chartprops.caption = footer;

	let footerExp = {}
	footerExp.align = "left"
	footerExp.verticalAlign = "bottom"
	footerExp.useHTML = false
	footerExp.text = footerNotesExp
  	chartprops.exporting.chartOptions.caption = footerExp;
	return chartprops
}

function formatValue(value, valuesMultiplier, decimalPrecision) {
	let val = _Highcharts.numberFormat(value, decimalPrecision)
	//let valMultiplied = value * valuesMultiplier
	if (valuesMultiplier == 1000000000) {
		val = _Highcharts.numberFormat(value, decimalPrecision) + ' Billion'
	} else if (valuesMultiplier == 1000000) {
		val = _Highcharts.numberFormat(value, decimalPrecision) + ' Million'
	} else if (valuesMultiplier == 1000) {
		val = _Highcharts.numberFormat(value, decimalPrecision) + ' Thousand'
	} 
	return val
}

function truncateLabel(text, maxWidth) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  // Match your treemap label font
  ctx.font = '16px Roboto';

  if (ctx.measureText(text).width <= maxWidth) {
    return text;
  }

  const ellipsis = '...';
  let truncated = text;

  while (
    truncated.length > 0 &&
    ctx.measureText(truncated + ellipsis).width > maxWidth
  ) {
    truncated = truncated.slice(0, -1);
  }

  return truncated + ellipsis;
}

async function refreshTreemapChart(type, chart, indicatorObj, chartSettings, dim, { onMoreClick }) {
	if(chart != null) {
		//all non xAxis series dim need to retrieve new dataset that meets filter criteria and rebuild chart
		let props = await defineChartProps(type, indicatorObj, chartSettings,  { onMoreClick })
		return props
	}
}

function getCategoryFilters(indicator) {
	let columns = indicator.columns.items.filter(col => {
		if(col.categoryLabelFlag || col.xAxisFlag) {
			//Parent in hierarchies should not be a category when it does not have subTotals - this would cause API error
			//if(col.childColumnName == null || col.includeParentFlag) 
				return col;
		}
	})
	return columns
}

//Set Xaxis to first dim, if it is a year limit Xaxus to the LATEST (edit:Tom) year. For all other dims, show all values
function getTreemapChartSettings(indicator, type) {
	console.log('getTreemapChartSettings')
	AppliedValues.setupAppliedValues(indicator)

	let seriesDim = indicator.columns.items.find(col => {
		if(col.treemapFlag == 1 && col.columnName !== 'Year') {
			return col;
		}
	})
	if(seriesDim == null) {
		throw new Error("Indicator categories are not valid for use as a series")
	}

	let xAxisDim = indicator.columns.items.find(col => {
		if(col.categoryLabelFlag == 1 &&
			col.columnName !== 'Year' &&
			col.columnName !== seriesDim.columnName &&
			(col.parentColumnName === null || col.parentColumnName !== seriesDim.columnName) &&
			(col.childColumnName === null || col.childColumnName !== seriesDim.columnName)) {
			return col;
		}
	})
	if(xAxisDim == null) {
		throw new Error("Indicator categories are not valid for use on the X Axis")
	}

	//set dim values used in dropdowns
	setDropdownDimValues(xAxisDim)
	setDropdownDimValues(seriesDim)

	//setup non-dropdown dims values
	indicator.columns.items.forEach(col => {
		//col is a data filter, so only allow one value to be selected

		if(col.categoryLabelFlag && col.columnName !== xAxisDim.columnName && col.columnName !== seriesDim.columnName) {
			const defaultVals = col.defaultValues.filter(x => col.distinctValues.includes(x))
			let val = null
			if (defaultVals.length > 0) {
				//use default value is no vals selected
				val = col.appliedValues.find(s => s.name == defaultVals[0])
			} else {
				const allVal = col.totalLabel
				const totalLabelFound = col.distinctValues.includes(allVal)
				if(totalLabelFound) {
					val = col.appliedValues.find(s => s.name == allVal)
				}
			}
			//If no default set and no total values set, use first value
			if(val == null) {
				//unless the columnName = Year then use the last value
				if(col.columnName === 'Year') {
					val = col.appliedValues[col.appliedValues.length - 1]
				} else {
					val = col.appliedValues[0]
				}
			}
			col.appliedValues.forEach(x => x.selected = x.name == val.name?true:false)
		}
	})
	return new ChartSettings(xAxisDim, seriesDim, type)
}

function setDropdownDimValues(col) {
	// AppliedValues.setupAppliedValues(indicator) already looks for defaults
	// Special rule for Treemap and Sankey sets dims to all values except totals
	const allVal = col.totalLabel
	const allValList = allVal.split(',').map(item => item.trim());
	const allValset = new Set(allValList);
	let totalsLabels = col.distinctValues.filter(item => allValset.has(item)); // [3, 4]
	if(totalsLabels.length > 0) {
		col.appliedValues.forEach(x => x.selected = totalsLabels.includes(x.name)?false:true)
	} else {
		try{
			Object.entries(col.distinctValues[0]).forEach(([key, arr]) => {
				const result = arr.filter(item => allValset.has(item));
				totalsLabels.push(...result);
			})
			col.appliedValues.forEach(x => x.selected = totalsLabels.includes(x.name)?true:false)
		} catch(e) {
			//don't do anything on failure
		}
	}
	return
}

function updateTreemapChartSettings(settings, indicator, type) {
	console.log('updateTreemapChartSettings')
	settings.type = type
	//If no seriesDim
	if(settings.seriesDim == null || settings.seriesDim == 'Year') {
		settings.seriesDim = indicator.columns.items.find(col => {
			if(col.treemapFlag && (settings.xAxisDim == null || col.columnName != settings.xAxisDim.columnName)) {
				//Parent in hierarchies should not be a category when it does not have subTotals - this would cause API error
				if(settings.xAxisDim != null && (settings.xAxisDim.childColumnName != null || settings.xAxisDim.parentColumnName != null) &&
				(col.childColumnName != null || col.parentColumnName != null )){
					null
				} else {
					return col;
				}
			}
		})
	}
	if(settings.seriesDim == null) {
		throw new Error("Indicator categories are not valid for use as a series")
	}
	//If no xAxisDim set it to Year, if no Year, set xaxis to first dim.  Default only the first value for xAxis to selected
	if(settings.xAxisDim == null || settings.xAxisDim.columnName == 'Year' || settings.seriesDim.columnName == settings.xAxisDim.columnName) {
		settings.xAxisDim = indicator.columns.items.find(col => {
			// if(col.categoryLabelFlag == 1 &&
			// 	col.columnName !== 'Year' &&
			// 	col.columnName !== settings.seriesDim.columnName &&
			// 	(col.parentColumnName === null || col.parentColumnName !== settings.seriesDim.columnName) &&
			// 	(col.childColumnName === null || col.childColumnName !== settings.seriesDim.columnName)) {
			// 	return col;
			// }
			//if col is in hierarchy, only default to child
			if(col.categoryLabelFlag == 1 &&
				col.columnName !== 'Year' &&
				col.columnName !== settings.seriesDim.columnName &&
				(col.parentColumnName === undefined || col.parentColumnName !== settings.seriesDim.columnName) &&
				col.childColumnName === undefined) {
				return col;
			}			
		})
	}
	if(settings.xAxisDim == null) {
		throw new Error("Indicator categories are not valid for use on the X Axis")
	}

	return settings;
}

function rebuildTreemapChartSettings(indicator, type, objReq) {
	console.log('rebuildTreemapChartSettings')
	const req = objReq.request
	let xAxisDim = indicator.columns.items.find(col => col.columnName == req.xAxis)
	let reqSeries = req.categories.find(cat => cat.selected && cat.categoryName !== req.xAxis)
	let seriesDim = indicator.columns.items.find(col => col.columnName == reqSeries.categoryName)

	AppliedValues.setupAppliedValues(indicator)
	AppliedValues.applyRequestFilters(indicator, type, objReq.request)

	const chartSettings = new ChartSettings(xAxisDim, seriesDim, type)
	return chartSettings
}

function validateRequestProps(obj, indicator) {
	return AppliedValues.validateRequestValues(obj, indicator, ChartTypeEnum.TREEMAP.chartName)
}

export {defineChartProps, refreshTreemapChart, getTreemapChartSettings, updateTreemapChartSettings, rebuildTreemapChartSettings, validateRequestProps, ChartSettings}
